import React from 'react'

import Asset, { ASSET_COLORS } from 'components/Asset'
import Exchange from 'components/Exchange'
import Tenant from 'components/Tenant'
import { TenantId } from 'contexts/Tenants'
import { useWallet } from 'contexts/Wallets'
import { isLoadedAndNonNullish } from 'utils/loadable'

type WalletId = number

type WalletProps = {
  walletId: WalletId
  tenantId: TenantId
  showTenant?: boolean
  showExchange?: boolean
  colorful?: boolean
}

const colorizeWalletName = (name: string): React.ReactNode => {
  const ticker = Object.keys(ASSET_COLORS).find(k => name.includes(k))
  if (!ticker) return name

  const parts = name.split(' ')
  const idx = parts.findIndex(p => p === ticker)
  if (idx < 0) return name

  return parts.map((p, i) => {
    const spacer = i > 0 ? ' ' : ''
    const text = `${spacer}${p}`
    if (i !== idx) {
      return <React.Fragment key={i}>{text}</React.Fragment>
    }

    const style = {
      padding: '0 2px',
      color: '#000',
      ...ASSET_COLORS[ticker],
    }

    return (
      <span key={i} style={style}>
        {text}
      </span>
    )
  })
}

const Wallet = ({
  walletId,
  tenantId,
  showTenant = false,
  showExchange = false,
  colorful = false,
}: WalletProps): JSX.Element => {
  const wallet = useWallet(tenantId, walletId)

  if (isLoadedAndNonNullish(wallet)) {
    const tenant = showTenant ? (
      <>
        <Tenant id={wallet.data.tenant_id} />{' '}
      </>
    ) : null
    const exchange = showExchange ? (
      <>
        <Exchange id={wallet.data.exchange_id} colorful={colorful} />{' '}
      </>
    ) : null
    const walletName = wallet.data.name ? (
      colorful ? (
        colorizeWalletName(wallet.data.name)
      ) : (
        wallet.data.name
      )
    ) : (
      <Asset
        asset={{ id: wallet.data.asset_id }}
        link={false}
        colorful={colorful}
      />
    )

    return (
      <span
        style={wallet.data.disabled ? { textDecoration: 'line-through' } : {}}
      >
        {tenant}
        {exchange}
        {walletName} {wallet.data.wallet_type}
      </span>
    )
  } else if (wallet.loading) {
    return <span>Loading...</span>
  }
  return <>[Unknown wallet]</>
}

export default Wallet
